import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem } from '@yola/ws-ui';

const {
  EditorHeader,
  ActionListItem,
  ActionListDivider,
  ActionList,
  ActionListLink,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  Icon,
} = designSystem;

const defaultItems = [];

const hide = () => Dropdown.hide();

const MainMenu = ({
  items = defaultItems,
  className = null,
  captions,
  showDetails = false,
  onShow = Function.prototype,
  onHide = Function.prototype,
  isHidden = false,
  href = null,
  iconGlyph = '',
}) => {
  const [activeId, setActiveId] = useState(0);

  const setActiveItem = useCallback((e) => {
    const index = Number(e.target.dataset.index);
    setActiveId(index);
  }, []);

  const renderItem = (item, key) => {
    const {
      onClick,
      href: itemHref,
      disabled,
      target,
      type,
      heading,
      iconGlyph: itemIconGlyph,
    } = item;

    if (type === 'divider') {
      return <ActionListDivider key={key} />;
    }

    const actionItemProps = {
      disabled,
      'data-index': key,
      title: heading,
      iconGlyph: itemIconGlyph || null,
      iconPlacement: itemIconGlyph ? 'left' : null,
      onMouseEnter: setActiveItem,
      onClick,
    };

    if (itemHref) {
      return <ActionListLink key={key} href={itemHref} target={target} {...actionItemProps} />;
    }

    return <ActionListItem key={key} {...actionItemProps} />;
  };

  const activeItem = items[activeId];

  const mainMenuClasses = classNames('ws-mainmenu', className, {
    'ws-mainmenu--hidden': isHidden,
  });

  return (
    <div className={mainMenuClasses}>
      {href && !isHidden && (
        <EditorHeader.NavItem href={href} target="blank" label={captions.name} />
      )}

      {!href && (
        <Dropdown
          placement="bottom-left"
          onShow={onShow}
          onHide={onHide}
          offsetMainAxis={18}
          offsetCrossAxis={-15}
        >
          {!isHidden && (
            <DropdownTrigger>
              <div className="ws-mainmenu__nav-item-with-icon">
                <EditorHeader.NavItem label={captions.name} />
                <div className="ws-mainmenu__nav-item-icon">
                  <Icon glyph={iconGlyph} />
                </div>
              </div>
            </DropdownTrigger>
          )}
          {isHidden && <div className="ws-mainmenu__blank" />}
          <DropdownContent>
            <ActionList>{items.map((item, key) => renderItem(item, key))}</ActionList>
            {activeItem && showDetails && (
              <div className="ws-mainmenu__description">
                <img
                  className="ws-mainmenu__description-image"
                  src={activeItem.image}
                  alt={activeItem.title}
                />
                <div className="ws-mainmenu__description-title">{activeItem.heading}</div>
                <div className="ws-mainmenu__description-text">{activeItem.description}</div>
              </div>
            )}
          </DropdownContent>
        </Dropdown>
      )}
    </div>
  );
};

MainMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      heading: PropTypes.string,
      description: PropTypes.string,
      title: PropTypes.string,
      iconGlyph: PropTypes.string,
      onClick: PropTypes.func,
      href: PropTypes.string,
      disabled: PropTypes.bool,
      target: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  captions: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  showDetails: PropTypes.bool,
  className: PropTypes.string,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  isHidden: PropTypes.bool,
  href: PropTypes.string,
  iconGlyph: PropTypes.string,
};

MainMenu.hide = hide;

export default MainMenu;
